/**
 * CollecteManager — gestion mairie « Points de collecte & Déchetterie » (GRC).
 *
 * Trois volets dans la ServiceCard « Services & vie quotidienne » :
 *   1. Points de dépôt : liste + carte (clic = pose un marqueur → coordonnées),
 *      modale de création/édition (type, adresse, horaires, déchets acceptés).
 *   2. Déchetterie : fiche unique (nom, adresse, horaires, déchets acceptés) +
 *      activation du dossier d'inscription + pièces demandées (libres) + PDF.
 *   3. Dossiers reçus : instruction (en cours / accepter + n° carte / refuser).
 *
 * Conforme au guide : zéro window.confirm (useConfirmDialog + {dialogElement}),
 * uploads via FormData. Expose onEtatChange pour le badge de la ServiceCard.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Alert, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { CollecteMap } from '@/components/collecte/CollecteMap';
import { COLLECTE_COLOR, COLLECTE_LABEL, COLLECTE_TYPES } from '@/components/collecte/collecteTypes';
import { api, ApiException } from '@/lib/api';
import type {
  CollectePoint, CollecteType, CollecteDechetterie, CollecteDossier, CollecteDossierStatut,
} from '@/lib/api';
import {
  Plus, Pencil, Trash2, AlertCircle, MapPin, Recycle, Upload, FileText, Check, X,
} from 'lucide-react';

type PointForm = {
  nom: string; type: CollecteType; adresse: string; horaires: string;
  dechetsAcceptes: string; note: string;
  latitude: number | null; longitude: number | null;
};

const emptyPoint: PointForm = {
  nom: '', type: 'verre', adresse: '', horaires: '', dechetsAcceptes: '', note: '',
  latitude: null, longitude: null,
};

export function CollecteManager({ onEtatChange }: { onEtatChange?: () => void }) {
  const navigate = useNavigate();
  const { confirm, dialogElement } = useConfirmDialog();

  const [points, setPoints] = useState<CollectePoint[]>([]);
  const [dech, setDech] = useState<CollecteDechetterie | null>(null);
  const [dossiers, setDossiers] = useState<CollecteDossier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // édition point
  const [pointEdit, setPointEdit] = useState<CollectePoint | 'new' | null>(null);
  const [pf, setPf] = useState<PointForm>(emptyPoint);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, d, dos] = await Promise.all([
        api.grc.collecte.listPoints(),
        api.grc.collecte.getDechetterie(),
        api.grc.collecte.listDossiers(),
      ]);
      setPoints(p.points);
      setDech(d);
      setDossiers(dos.dossiers);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  // ---- Points ----
  function openPoint(p: CollectePoint | 'new') {
    if (p === 'new') { setPf(emptyPoint); }
    else {
      setPf({
        nom: p.nom, type: p.type, adresse: p.adresse ?? '', horaires: p.horaires ?? '',
        dechetsAcceptes: p.dechetsAcceptes.join(', '), note: p.note ?? '',
        latitude: p.latitude, longitude: p.longitude,
      });
    }
    setPointEdit(p);
  }

  async function savePoint() {
    const body = {
      nom: pf.nom.trim(),
      type: pf.type,
      adresse: pf.adresse.trim() || null,
      horaires: pf.horaires.trim() || null,
      dechetsAcceptes: pf.dechetsAcceptes.split(',').map((s) => s.trim()).filter(Boolean),
      note: pf.note.trim() || null,
      latitude: pf.latitude,
      longitude: pf.longitude,
    };
    try {
      if (pointEdit === 'new') await api.grc.collecte.createPoint(body);
      else if (pointEdit) await api.grc.collecte.updatePoint(pointEdit.id, body);
      setPointEdit(null);
      await reload();
      onEtatChange?.();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur d\'enregistrement.');
    }
  }

  async function deletePoint(p: CollectePoint) {
    const ok = await confirm({
      title: 'Supprimer ce point ?',
      description: `« ${p.nom} » sera retiré de la carte.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.collecte.removePoint(p.id); await reload(); onEtatChange?.(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur.'); }
  }

  // ---- Déchetterie ----
  const [dechForm, setDechForm] = useState<{
    nom: string; adresse: string; horaires: string; dechetsAcceptes: string;
    inscriptionActive: boolean; inscriptionIntro: string;
    latitude: number | null; longitude: number | null;
    cartePrefixe: string; carteProchainNumero: number; carteInclureAnnee: boolean;
    carteValiditeMois: number;
    modeDelivranceDefaut: 'dematerialisee' | 'physique' | 'les_deux';
  } | null>(null);
  const formulaireRef = useRef<HTMLInputElement | null>(null);

  function openDechetterie() {
    setDechForm({
      nom: dech?.nom ?? '', adresse: dech?.adresse ?? '', horaires: dech?.horaires ?? '',
      dechetsAcceptes: (dech?.dechetsAcceptes ?? []).join(', '),
      inscriptionActive: dech?.inscriptionActive ?? false,
      inscriptionIntro: dech?.inscriptionIntro ?? '',
      latitude: dech?.latitude ?? null, longitude: dech?.longitude ?? null,
      cartePrefixe: dech?.cartePrefixe ?? 'DECH',
      carteProchainNumero: dech?.carteProchainNumero ?? 1,
      carteInclureAnnee: dech?.carteInclureAnnee ?? true,
      carteValiditeMois: dech?.carteValiditeMois ?? 12,
      modeDelivranceDefaut: dech?.modeDelivranceDefaut ?? 'dematerialisee',
    });
  }

  async function saveDechetterie() {
    if (!dechForm) return;
    try {
      await api.grc.collecte.saveDechetterie({
        nom: dechForm.nom.trim(),
        adresse: dechForm.adresse.trim() || null,
        horaires: dechForm.horaires.trim() || null,
        dechetsAcceptes: dechForm.dechetsAcceptes.split(',').map((s) => s.trim()).filter(Boolean),
        inscriptionActive: dechForm.inscriptionActive,
        inscriptionIntro: dechForm.inscriptionIntro.trim() || null,
        latitude: dechForm.latitude, longitude: dechForm.longitude,
        cartePrefixe: dechForm.cartePrefixe.trim() || 'DECH',
        carteProchainNumero: dechForm.carteProchainNumero,
        carteInclureAnnee: dechForm.carteInclureAnnee,
        carteValiditeMois: dechForm.carteValiditeMois,
        modeDelivranceDefaut: dechForm.modeDelivranceDefaut,
      });
      setDechForm(null);
      await reload();
      onEtatChange?.();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur d\'enregistrement.');
    }
  }

  async function uploadFormulaire(file: File) {
    try { await api.grc.collecte.uploadFormulaire(file); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur d\'envoi.'); }
  }

  // pièces attendues
  const [nouvellePiece, setNouvellePiece] = useState('');
  async function addPiece() {
    const lib = nouvellePiece.trim();
    if (!lib) return;
    try { await api.grc.collecte.addPieceAttendue({ libelle: lib }); setNouvellePiece(''); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur.'); }
  }
  async function removePiece(id: string) {
    try { await api.grc.collecte.removePieceAttendue(id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur.'); }
  }

  // ---- Dossiers ----
  const [instruire, setInstruire] = useState<CollecteDossier | null>(null);
  const [numeroCarte, setNumeroCarte] = useState('');
  const [reponse, setReponse] = useState('');

  async function statuerDossier(d: CollecteDossier, statut: CollecteDossierStatut) {
    try {
      await api.grc.collecte.changerStatutDossier(d.id, {
        statut,
        numeroCarte: statut === 'acceptee' ? (numeroCarte.trim() || null) : null,
        reponseStaff: statut === 'refusee' ? (reponse.trim() || null) : null,
      });
      setInstruire(null); setNumeroCarte(''); setReponse('');
      await reload();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur.'); }
  }

  async function supprimerDossier(d: CollecteDossier) {
    const ok = await confirm({
      title: 'Supprimer ce dossier ?',
      description: 'Les pièces jointes seront également supprimées.',
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.collecte.removeDossier(d.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur.'); }
  }

  const dossiersEnCours = dossiers.filter((d) => ['nouvelle', 'en_cours'].includes(d.statut));

  if (loading) return <p className="text-sm text-ink-muted">Chargement…</p>;

  // Points affichés sur la carte (déchetterie incluse si géolocalisée)
  const pointsCarte: CollectePoint[] = [...points];
  if (dech && dech.latitude !== null && dech.longitude !== null) {
    pointsCarte.push({
      id: `dech-${dech.id}`, nom: dech.nom, type: 'dechetterie',
      adresse: dech.adresse, latitude: dech.latitude, longitude: dech.longitude,
      horaires: dech.horaires, dechetsAcceptes: dech.dechetsAcceptes, note: null,
      affiche: true, ordre: 9999,
    });
  }

  return (
    <div className="space-y-6">
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

      {/* ── POINTS ── */}
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">Points de dépôt sur la carte</h3>
          <Button variant="ghost" size="sm" onClick={() => openPoint('new')}>
            <Plus className="h-4 w-4" /> Point
          </Button>
        </div>
        <div className="mt-3">
          <CollecteMap points={pointsCarte} height={260} />
        </div>
        {points.length === 0 ? (
          <p className="mt-3 text-sm text-ink-dim">Aucun point. Ajoutez un container à verre, une borne textile…</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {points.map((p) => (
              <li key={p.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ background: COLLECTE_COLOR[p.type] }} aria-hidden="true" />
                  <span className="font-medium text-ink">{p.nom}</span>
                  <span className="ml-auto flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openPoint(p)} title="Modifier"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deletePoint(p)} title="Supprimer"><Trash2 className="h-4 w-4" /></Button>
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5 pl-5">
                  <span className="rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">{COLLECTE_LABEL[p.type]}</span>
                  {p.latitude !== null && p.longitude !== null && (
                    <span className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">
                      <MapPin className="h-3 w-3" />{p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── DÉCHETTERIE ── */}
      <div className="border-t border-border pt-5">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Recycle className="h-4 w-4" /> Déchetterie de la commune
          </h3>
          <Button variant="ghost" size="sm" onClick={openDechetterie}>
            {dech ? <><Pencil className="h-4 w-4" /> Modifier</> : <><Plus className="h-4 w-4" /> Renseigner</>}
          </Button>
        </div>

        {dech ? (
          <div className="mt-3 rounded-r-lg border border-border bg-surface/50 p-3">
            <div className="font-medium text-ink">{dech.nom}</div>
            {dech.adresse && <div className="text-sm text-ink-muted">{dech.adresse}</div>}
            {dech.horaires && <div className="mt-1 text-xs text-ink-dim">🕒 {dech.horaires}</div>}
            {dech.dechetsAcceptes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {dech.dechetsAcceptes.map((d, i) => (
                  <span key={i} className="rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted">{d}</span>
                ))}
              </div>
            )}

            {/* Dossier d'inscription config */}
            <div className="mt-3 border-t border-border pt-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink">Dossier d'inscription</span>
                {dech.inscriptionActive
                  ? <Badge variant="success" size="sm">Activé</Badge>
                  : <Badge variant="neutral" size="sm">Désactivé</Badge>}
              </div>

              {dech.inscriptionActive && (
                <>
                  <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-ink-dim">Pièces demandées</div>
                  <ul className="mt-1.5 space-y-1.5">
                    {dech.piecesAttendues.map((pa) => (
                      <li key={pa.id} className="flex items-center gap-2 rounded-r-md border border-border bg-bg px-2.5 py-1.5 text-sm">
                        <span className="text-ink">{pa.libelle}</span>
                        <button type="button" onClick={() => removePiece(pa.id)} className="ml-auto text-ink-dim hover:text-urgent" title="Retirer">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={nouvellePiece}
                      onChange={(e) => setNouvellePiece(e.target.value)}
                      placeholder="Ajouter une pièce (ex. Avis d'imposition)"
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" onClick={addPiece} aria-label="Ajouter cette pièce"><Plus className="h-4 w-4" aria-hidden="true" /></Button>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input ref={formulaireRef} type="file" accept="application/pdf" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFormulaire(f); e.target.value = ''; }} />
                    <Button variant="ghost" size="sm" onClick={() => formulaireRef.current?.click()}>
                      <Upload className="h-4 w-4" /> {dech.aFormulaire ? 'Remplacer le formulaire' : 'Joindre un formulaire PDF'}
                    </Button>
                    {dech.aFormulaire && (
                      <span className="inline-flex items-center gap-1 text-xs text-ink-dim">
                        <FileText className="h-3.5 w-3.5" /> {dech.formulaireNom ?? 'formulaire.pdf'}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-ink-dim">Non renseignée. Une fois remplie, elle apparaît côté habitant (fiche + carte).</p>
        )}
      </div>

      {/* ── DOSSIERS REÇUS (résumé + lien vers la page dédiée) ── */}
      {dech?.inscriptionActive && (
        <div className="border-t border-border pt-5">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-ink">
              Dossiers d'inscription reçus
              {dossiersEnCours.length > 0 && <Badge variant="warning" size="sm" className="ml-2">{dossiersEnCours.length} à traiter</Badge>}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/grc/dossiers-dechetterie')}>
              Voir &amp; instruire →
            </Button>
          </div>
          <p className="mt-1 text-xs text-ink-dim">
            {dossiers.length === 0
              ? 'Aucun dossier pour l\'instant.'
              : `${dossiers.length} dossier(s) au total. L'instruction (accepter, refuser, numéro de carte) se fait dans Dossiers → Dossiers déchetterie.`}
          </p>
        </div>
      )}

      {/* ── Modale point ── */}
      {pointEdit && (
        <Dialog
          open onClose={() => setPointEdit(null)}
          title={pointEdit === 'new' ? 'Nouveau point de dépôt' : 'Modifier le point'}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPointEdit(null)}>Annuler</Button>
              <Button variant="primary" onClick={savePoint} disabled={!pf.nom.trim()}>Enregistrer</Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label="Nom" htmlFor="pt-nom" required>
              <Input id="pt-nom" value={pf.nom} onChange={(e) => setPf({ ...pf, nom: e.target.value })} placeholder="Container à verre — Place de l'Église" />
            </Field>
            <Field label="Type" htmlFor="pt-type" required>
              <select id="pt-type" value={pf.type} onChange={(e) => setPf({ ...pf, type: e.target.value as CollecteType })}
                className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink">
                {COLLECTE_TYPES.map((t) => <option key={t} value={t}>{COLLECTE_LABEL[t]}</option>)}
              </select>
            </Field>
            <Field label="Adresse" htmlFor="pt-adr">
              <Input id="pt-adr" value={pf.adresse} onChange={(e) => setPf({ ...pf, adresse: e.target.value })} />
            </Field>
            <Field label="Horaires" htmlFor="pt-hor" hint="Ex. accès libre 24h/24">
              <Input id="pt-hor" value={pf.horaires} onChange={(e) => setPf({ ...pf, horaires: e.target.value })} />
            </Field>
            <Field label="Déchets acceptés" htmlFor="pt-dech" hint="Séparés par des virgules">
              <Input id="pt-dech" value={pf.dechetsAcceptes} onChange={(e) => setPf({ ...pf, dechetsAcceptes: e.target.value })} placeholder="Bouteilles, bocaux" />
            </Field>
            <Field label="Note / consigne" htmlFor="pt-note">
              <Input id="pt-note" value={pf.note} onChange={(e) => setPf({ ...pf, note: e.target.value })} placeholder="Pas de vaisselle ni ampoules" />
            </Field>
            <div>
              <div className="mb-1 text-xs text-ink-muted">Position sur la carte (cliquez pour poser)</div>
              <CollecteMap
                points={pointsCarte}
                height={200}
                onPick={(c) => setPf({ ...pf, latitude: c.lat, longitude: c.lng })}
                draft={pf.latitude !== null && pf.longitude !== null ? { lat: pf.latitude, lng: pf.longitude } : null}
              />
              {pf.latitude !== null && pf.longitude !== null && (
                <div className="mt-1 text-xs text-ink-dim">📍 {pf.latitude.toFixed(5)}, {pf.longitude.toFixed(5)}</div>
              )}
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Modale déchetterie ── */}
      {dechForm && (
        <Dialog
          open onClose={() => setDechForm(null)}
          title="Déchetterie de la commune"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDechForm(null)}>Annuler</Button>
              <Button variant="primary" onClick={saveDechetterie} disabled={!dechForm.nom.trim()}>Enregistrer</Button>
            </>
          }
        >
          <div className="space-y-3">
            <Field label="Nom / gestionnaire" htmlFor="dc-nom" required>
              <Input id="dc-nom" value={dechForm.nom} onChange={(e) => setDechForm({ ...dechForm, nom: e.target.value })} placeholder="Déchetterie SMICTOM de Saintonge" />
            </Field>
            <Field label="Adresse" htmlFor="dc-adr">
              <Input id="dc-adr" value={dechForm.adresse} onChange={(e) => setDechForm({ ...dechForm, adresse: e.target.value })} />
            </Field>
            <Field label="Horaires" htmlFor="dc-hor">
              <Input id="dc-hor" value={dechForm.horaires} onChange={(e) => setDechForm({ ...dechForm, horaires: e.target.value })} placeholder="Lun–Sam 9h–12h / 14h–18h" />
            </Field>
            <Field label="Déchets acceptés" htmlFor="dc-dech" hint="Séparés par des virgules">
              <Input id="dc-dech" value={dechForm.dechetsAcceptes} onChange={(e) => setDechForm({ ...dechForm, dechetsAcceptes: e.target.value })} placeholder="Gravats, cartons, déchets verts, encombrants, DEEE" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={dechForm.inscriptionActive}
                onChange={(e) => setDechForm({ ...dechForm, inscriptionActive: e.target.checked })} />
              Activer le dossier d'inscription (carte d'accès / n° adhérent)
            </label>
            {dechForm.inscriptionActive && (
              <Field label="Consigne affichée à l'habitant" htmlFor="dc-intro">
                <Input id="dc-intro" value={dechForm.inscriptionIntro} onChange={(e) => setDechForm({ ...dechForm, inscriptionIntro: e.target.value })} placeholder="Déposez vos pièces, nous délivrons votre carte." />
              </Field>
            )}
            {dechForm.inscriptionActive && (
              <div className="rounded-r-md border border-border bg-bg p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-dim">Numérotation des cartes</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Préfixe" htmlFor="dc-pref">
                    <Input id="dc-pref" value={dechForm.cartePrefixe} onChange={(e) => setDechForm({ ...dechForm, cartePrefixe: e.target.value })} placeholder="DECH" />
                  </Field>
                  <Field label="Prochain numéro" htmlFor="dc-num" hint="Si vous avez déjà des cartes">
                    <Input id="dc-num" type="number" min={1} value={dechForm.carteProchainNumero}
                      onChange={(e) => setDechForm({ ...dechForm, carteProchainNumero: Math.max(1, Number(e.target.value) || 1) })} />
                  </Field>
                </div>
                <label className="mt-1 flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={dechForm.carteInclureAnnee}
                    onChange={(e) => setDechForm({ ...dechForm, carteInclureAnnee: e.target.checked })} />
                  Inclure l'année dans le numéro
                </label>
                <div className="mt-2">
                  <Field label="Durée de validité de la carte (mois)" htmlFor="dc-valid" hint="0 = sans limite de durée">
                    <Input id="dc-valid" type="number" min={0} max={120} value={dechForm.carteValiditeMois}
                      onChange={(e) => setDechForm({ ...dechForm, carteValiditeMois: Math.max(0, Number(e.target.value) || 0) })} />
                  </Field>
                </div>
                <div className="mt-2">
                  <Field label="Mode de délivrance par défaut" htmlFor="dc-mode-def" hint="Pré-rempli à la validation de chaque dossier (modifiable au cas par cas)">
                    <select id="dc-mode-def" value={dechForm.modeDelivranceDefaut}
                      onChange={(e) => setDechForm({ ...dechForm, modeDelivranceDefaut: e.target.value as typeof dechForm.modeDelivranceDefaut })}
                      className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink">
                      <option value="dematerialisee">Dématérialisée (attestation PDF)</option>
                      <option value="physique">Physique (carte remise en mairie)</option>
                      <option value="les_deux">Les deux</option>
                    </select>
                  </Field>
                </div>
                <p className="mt-1.5 text-xs text-ink-dim">
                  Exemple : <span className="text-ink-muted">{dechForm.cartePrefixe || 'DECH'}{dechForm.carteInclureAnnee ? `-${new Date().getFullYear()}` : ''}-{String(dechForm.carteProchainNumero).padStart(4, '0')}</span>.
                  Généré automatiquement à chaque acceptation (modifiable à la main).
                </p>
              </div>
            )}
            <div>
              <div className="mb-1 text-xs text-ink-muted">Position sur la carte (cliquez pour poser)</div>
              <CollecteMap
                points={pointsCarte}
                height={200}
                onPick={(c) => setDechForm({ ...dechForm, latitude: c.lat, longitude: c.lng })}
                draft={dechForm.latitude !== null && dechForm.longitude !== null ? { lat: dechForm.latitude, lng: dechForm.longitude } : null}
              />
            </div>
          </div>
        </Dialog>
      )}

      {/* ── Modale instruction dossier ── */}
      {instruire && (
        <Dialog
          open onClose={() => setInstruire(null)}
          title={`Dossier — ${instruire.demandeurNom ?? 'Administré'}`}
          footer={<Button variant="ghost" onClick={() => setInstruire(null)}>Fermer</Button>}
        >
          <div className="space-y-3">
            <div className="rounded-r-md border border-border bg-bg p-3 text-sm">
              {instruire.demandeurEmail && <div className="text-ink-muted">{instruire.demandeurEmail}</div>}
              {instruire.demandeurTel && <div className="text-ink-muted">{instruire.demandeurTel}</div>}
              {instruire.demandeurAdresse && <div className="text-ink-muted">{instruire.demandeurAdresse}</div>}
              {instruire.message && <div className="mt-1 text-ink">{instruire.message}</div>}
            </div>

            {instruire.pieces.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-ink-dim">Pièces déposées</div>
                <ul className="mt-1.5 flex flex-wrap gap-2">
                  {instruire.pieces.map((pj) => (
                    <li key={pj.id}>
                      {pj.url
                        ? <a href={pj.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted hover:text-ink"><FileText className="h-3 w-3" />{pj.libelle ?? pj.nomFichier ?? 'pièce'}</a>
                        : <span className="text-xs text-ink-dim">{pj.libelle ?? pj.nomFichier}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Field label="Numéro de carte / adhérent (si accepté)" htmlFor="ins-num">
              <Input id="ins-num" value={numeroCarte} onChange={(e) => setNumeroCarte(e.target.value)} placeholder="Ex. DECH-2026-0142" />
            </Field>
            <Field label="Motif (si refusé)" htmlFor="ins-rep">
              <Input id="ins-rep" value={reponse} onChange={(e) => setReponse(e.target.value)} placeholder="Justificatif de domicile manquant" />
            </Field>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => statuerDossier(instruire, 'en_cours')}>Marquer en cours</Button>
              <Button variant="primary" size="sm" onClick={() => statuerDossier(instruire, 'acceptee')}><Check className="h-4 w-4" /> Accepter</Button>
              <Button variant="danger" size="sm" onClick={() => statuerDossier(instruire, 'refusee')}><X className="h-4 w-4" /> Refuser</Button>
            </div>
          </div>
        </Dialog>
      )}

      {dialogElement}
    </div>
  );
}
